import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useSession } from '@/hooks/useSession';
import { isFullAdmin } from '@/types/session';
import { useUsers, useUser, useUpdateUser } from '../hooks';
import { userKeys } from '../hooks/queryKeys';
import { getUsers } from '../api';
import { UsersFilters } from '../components/UsersFilters';
import { UsersTable } from '../components/UsersTable';
import { UserFormModal, CreateUserModal } from '../components/modals';
import { exportUsersToCSV } from '../utils/csvExport';
import type { User, UsersFilters as UsersFiltersType, UpdateUserInput } from '../types';

type StatusFilter = 'all' | 'true' | 'false';

const COLUMN_FILTERS_STORAGE_KEY = 'atek-users-column-filters';

export default function AllUsersPage() {
  // Session for admin level checks
  const { user: sessionUser } = useSession();
  const isAdmin = isFullAdmin(sessionUser);
  // Mirrors the web_api_server contact-info tier: sales_manager may edit
  // contact fields (incl. email) alongside admins.
  const isContactManager = !!sessionUser?.postgres_roles?.includes('sales_manager');
  const isSuperAdmin = isAdmin;

  const queryClient = useQueryClient();

  // Server-side filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [organisationId, setOrganisationId] = useState('all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState<UsersFiltersType['sortKey']>('email');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  // Column filter state (client-side refinement + promoted to server search)
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(COLUMN_FILTERS_STORAGE_KEY) || '{}');
      // Organisations is now driven by the dedicated dropdown, not a column text filter
      delete parsed.organisations;
      return parsed;
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem(COLUMN_FILTERS_STORAGE_KEY, JSON.stringify(columnFilters));
  }, [columnFilters]);

  const setColumnFilter = (key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Modal state
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Export-all state
  const [isExportingAll, setIsExportingAll] = useState(false);

  // Debounce toolbar search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Promote text column filters to server-side search
  // Date-range values contain ">>" and selects start with "[" — skip those
  const columnSearchText = useMemo(() => {
    return Object.entries(columnFilters)
      .filter(([, value]) => {
        if (!value) return false;
        if (value.includes('>>')) return false; // date-range
        if (value.startsWith('[')) return false; // multi-select
        return true;
      })
      .map(([, value]) => value)
      .join(' ');
  }, [columnFilters]);

  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedColumnSearch(columnSearchText);
      if (columnSearchText) setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [columnSearchText]);

  const combinedSearch = [debouncedSearch, debouncedColumnSearch].filter(Boolean).join(' ') || undefined;

  // Build filters object
  const filters: UsersFiltersType = {
    search: combinedSearch,
    organisation: organisationId !== 'all' ? organisationId : undefined,
    enabled: status,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
  };

  // Queries — every lookup keyed by PG UUID (V4 PG-only user management).
  const usersQuery = useUsers(filters);
  const editingUserQuery = useUser(editingUser?.id ?? editingUser?.pg_id);

  // Mutations
  const updateUserMutation = useUpdateUser();

  const users = usersQuery.data?.users || [];
  const total = usersQuery.data?.total || 0;

  // Handlers
  const handleSort = (key: UsersFiltersType['sortKey']) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const handleOrganisationChange = (value: string) => {
    setOrganisationId(value);
    setPage(0);
  };

  const handleStatusChange = (value: StatusFilter) => {
    setStatus(value);
    setPage(0);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  const handleUpdateUser = async (data: UpdateUserInput) => {
    if (!editingUser) return;
    const pgId = editingUser.id ?? editingUser.pg_id;
    if (!pgId) return;
    await updateUserMutation.mutateAsync({ userId: pgId, data });
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: userKeys.lists() });
  };

  const handleExportAll = async () => {
    try {
      setIsExportingAll(true);
      const allUsers: User[] = [];
      const batchSize = 100;
      let pageNum = 0;
      let keepGoing = true;

      while (keepGoing) {
        // eslint-disable-next-line no-await-in-loop
        const batch = await getUsers({
          pageNumber: pageNum,
          pageLength: batchSize,
          search: combinedSearch,
          organisation: organisationId !== 'all' ? organisationId : undefined,
          enabled: status,
          sortKey,
          sortDirection,
        });
        const batchUsers = batch.users || [];
        allUsers.push(...batchUsers);
        keepGoing = batchUsers.length === batchSize;
        pageNum += 1;
        if (pageNum > 500) break; // safety: 50K users max
      }

      if (allUsers.length === 0) {
        toast.error('No users to export');
        return;
      }

      exportUsersToCSV(allUsers, 'users-all');
      toast.success(`Exported ${allUsers.length} users to CSV`);
    } catch {
      toast.error('Failed to export all users');
    } finally {
      setIsExportingAll(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            All Users <small>Platform user management</small>
          </h1>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Create User
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <UsersFilters
          search={search}
          onSearchChange={setSearch}
          status={status}
          onStatusChange={handleStatusChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {total} user{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {usersQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">Error loading users. Please try again.</p>
          </CardContent>
        </Card>
      ) : (
        <UsersTable
          users={users}
          total={total}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onEditUser={setEditingUser}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          selectedOrgId={organisationId !== 'all' ? organisationId : undefined}
          onSelectedOrgChange={handleOrganisationChange}
          columnFilters={columnFilters}
          onColumnFilterChange={setColumnFilter}
          onClearColumnFilters={() => {
            setColumnFilters({});
            handleOrganisationChange('all');
          }}
          onRefresh={handleRefresh}
          isRefreshing={usersQuery.isFetching}
          isLoading={usersQuery.isLoading}
          onExportAll={handleExportAll}
          isExportingAll={isExportingAll}
        />
      )}

      {/* Edit Modal */}
      <UserFormModal
        open={!!editingUser}
        onOpenChange={(open) => {
          if (!open) setEditingUser(null);
        }}
        user={editingUserQuery.data ?? editingUser}
        onSubmit={handleUpdateUser}
        isSubmitting={updateUserMutation.isPending}
        canDeleteEntirely={isSuperAdmin}
        isFullAdmin={isAdmin}
        canEditEmail={isAdmin || isContactManager}
      />

      {/* Create Modal */}
      <CreateUserModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
      />
    </div>
  );
}
